import { env, isMetaPixelConfigured, isPosthogConfigured } from "@/lib/env";
import { ANALYTICS_EVENTS, type AnalyticsEventName, resolveRouteGroup } from "./events";
import { createMetaAdapter } from "./metaAdapter";
import { createPosthogAdapter } from "./posthogAdapter";
import type { AnalyticsAdapter, AnalyticsProperties } from "./types";
import { isNoticeProductOnly, redactNoticeProperties } from "./noticePrivacy";

function sanitizeProperties(properties: AnalyticsProperties = {}) {
  const sanitized: AnalyticsProperties = {};

  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

function buildDefaultAdapters(): AnalyticsAdapter[] {
  const analyticsEnabled = env.appEnv === "production";

  return [
    createMetaAdapter({
      enabled: analyticsEnabled && isMetaPixelConfigured,
      pixelId: env.metaPixelId,
      windowObject: typeof window === "undefined" ? undefined : window,
      documentObject: typeof document === "undefined" ? undefined : document,
    }),
    createPosthogAdapter({
      enabled: analyticsEnabled && isPosthogConfigured,
      apiKey: env.posthogKey,
      apiHost: env.posthogHost,
    }),
  ];
}

export function createAnalyticsClient(adapters: AnalyticsAdapter[], productOnly = isNoticeProductOnly) {
  let initialized = false;
  let lastPagePath: string | null = null;

  function track(name: AnalyticsEventName, properties: AnalyticsProperties = {}) {
    const notice = productOnly();
    const sanitized = sanitizeProperties(notice
      ? { ...redactNoticeProperties(properties), analytics_purpose: "service_period_notice" } as AnalyticsProperties
      : properties);
    adapters.filter(adapter => !notice || adapter.destination !== "advertising")
      .forEach((adapter) => adapter.track(name, sanitized));
  }

  return {
    init() {
      if (initialized) {
        return;
      }

      adapters.filter(adapter => !productOnly() || adapter.destination !== "advertising")
        .forEach((adapter) => adapter.init());
      initialized = true;
    },
    track,
    trackPageView(path: string, properties: AnalyticsProperties = {}) {
      if (!path || lastPagePath === path) {
        return;
      }

      lastPagePath = path;
      track(ANALYTICS_EVENTS.PAGE_VIEWED, {
        path,
        routeGroup: resolveRouteGroup(path),
        ...properties,
      });
    },
    identify(userId: string, properties: AnalyticsProperties = {}) {
      const notice = productOnly();
      const sanitized = sanitizeProperties(notice ? redactNoticeProperties(properties) as AnalyticsProperties : properties);
      adapters.filter(adapter => !notice || adapter.destination !== "advertising")
        .forEach((adapter) => adapter.identify(userId, sanitized));
    },
    resetAnalytics() {
      lastPagePath = null;
      adapters.forEach((adapter) => adapter.reset());
    },
  };
}

const analyticsClient = createAnalyticsClient(buildDefaultAdapters());

export function initAnalytics() {
  analyticsClient.init();
}

export function track(name: AnalyticsEventName, properties: AnalyticsProperties = {}) {
  analyticsClient.track(name, properties);
}

export function trackPageView(path: string, properties: AnalyticsProperties = {}) {
  analyticsClient.trackPageView(path, properties);
}

export function identify(userId: string, properties: AnalyticsProperties = {}) {
  analyticsClient.identify(userId, properties);
}

export function resetAnalytics() {
  analyticsClient.resetAnalytics();
}
